/**
 * Interval Drill — free-practice mode.
 *
 * Cadence establishes the key, two notes play, user identifies the interval.
 * Self-directed: no session machine, no FSRS by default.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import {
  buildIntervalExerciseRound,
  buildIntervalBlockPlan,
  CADENCE_TEMPO_OPTS,
  DEFAULT_INTERVAL_EXERCISE_CONFIG,
  intervalDrillItemId,
  intervalDrillStatsKey,
  INTERVAL_POOL,
  INTERVAL_PRESETS,
  INTERVAL_SYMBOLS_ORDERED,
  IONIAN_SOLFEGE,
  type IntervalExerciseConfig,
  type IntervalExerciseRound,
  type IntervalSymbol,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import { StagePips } from "@music-app/ui-components";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDrillStats,
  recordReview as dbRecordReview,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { Score } from "@music-app/notation";
import type { VexNote } from "@music-app/notation";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { getChallengeData, recordChallengeAttempt } from "@music-app/persistence";

// ─── Constants ────────────────────────────────────────────────────────────────

const CADENCE_PIP_COUNT = 4;
const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── Tonic options ────────────────────────────────────────────────────────────

const TONIC_OPTIONS: ReadonlyArray<{ midi: number; label: string }> = [
  { midi: 60, label: "C" },
  { midi: 67, label: "G" },
  { midi: 62, label: "D" },
  { midi: 69, label: "A" },
  { midi: 64, label: "E" },
  { midi: 65, label: "F" },
  { midi: 70, label: "B♭" },
  { midi: 63, label: "E♭" },
  { midi: 68, label: "A♭" },
  { midi: 61, label: "D♭" },
];

// ─── Interval group presets ───────────────────────────────────────────────────

type GroupKey =
  | "seconds"
  | "thirds"
  | "fourths-fifths"
  | "tritone"
  | "sixths"
  | "sevenths"
  | "octave";

interface IntervalGroup {
  key: GroupKey;
  label: string;
  symbols: IntervalSymbol[];
}

const INTERVAL_GROUPS: IntervalGroup[] = [
  { key: "seconds",         label: "Seconds",          symbols: ["m2", "M2"] },
  { key: "thirds",          label: "Thirds",           symbols: ["m3", "M3"] },
  { key: "fourths-fifths",  label: "Fourths & Fifths", symbols: ["P4", "P5"] },
  { key: "tritone",         label: "Tritone",          symbols: ["TT"] },
  { key: "sixths",          label: "Sixths",           symbols: ["m6", "M6"] },
  { key: "sevenths",        label: "Sevenths",         symbols: ["m7", "M7"] },
  { key: "octave",          label: "Octave",           symbols: ["P8"] },
];

const GROUP_PRESETS: Record<"consonants" | "diatonic" | "all", GroupKey[]> = {
  consonants: ["thirds", "fourths-fifths", "sixths", "octave"],
  diatonic:   ["seconds", "thirds", "fourths-fifths", "sixths", "sevenths", "octave"],
  all:        ["seconds", "thirds", "fourths-fifths", "tritone", "sixths", "sevenths", "octave"],
};

function groupsToIntervals(groups: Set<GroupKey>): IntervalSymbol[] {
  return INTERVAL_SYMBOLS_ORDERED.filter((sym) =>
    INTERVAL_GROUPS.some((g) => groups.has(g.key) && g.symbols.includes(sym)),
  );
}

// ─── State machine ────────────────────────────────────────────────────────────

type IntervalExercisePhase =
  | "config"
  | "playing"
  | "thinking"
  | "awaiting"
  | "reveal"
  | "graded"
  | "done";

interface SessionStat {
  correct: number;
  played: number;
}

interface IntervalExerciseState {
  phase: IntervalExercisePhase;
  config: IntervalExerciseConfig;
  sessionLength: number;
  round: IntervalExerciseRound | null;
  roundNumber: number;
  correctCount: number;
  selectedSymbol: IntervalSymbol | null;
  wasCorrect: boolean | null;
  wasTimeout: boolean;
  sessionStats: Partial<Record<IntervalSymbol, SessionStat>>;
}

type IntervalExerciseAction =
  | { type: "START"; config: IntervalExerciseConfig; sessionLength: number; round: IntervalExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "THINKING" }
  | { type: "SELECT"; symbol: IntervalSymbol }
  | { type: "TIMEOUT" }
  | { type: "NEXT_ROUND"; round: IntervalExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: IntervalExerciseState = {
  phase: "config",
  config: DEFAULT_INTERVAL_EXERCISE_CONFIG,
  sessionLength: DEFAULT_SESSION_LENGTH,
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selectedSymbol: null,
  wasCorrect: null,
  wasTimeout: false,
  sessionStats: {},
};

function addStat(
  stats: Partial<Record<IntervalSymbol, SessionStat>>,
  symbol: IntervalSymbol,
  correct: boolean | null,
): Partial<Record<IntervalSymbol, SessionStat>> {
  const prev = stats[symbol] ?? { correct: 0, played: 0 };
  return {
    ...stats,
    [symbol]: {
      played: prev.played + 1,
      correct: prev.correct + (correct === true ? 1 : 0),
    },
  };
}

function drillIntervalReducer(
  state: IntervalExerciseState,
  action: IntervalExerciseAction,
): IntervalExerciseState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };

    case "THINKING":
      return state.phase === "playing" ? { ...state, phase: "thinking" } : state;

    case "PLAYBACK_DONE": {
      if (state.phase !== "playing" && state.phase !== "thinking") return state;
      if (state.config.drillMode === "listening" && state.round) {
        return {
          ...state,
          phase: "reveal",
          sessionStats: addStat(state.sessionStats, state.round.symbol, null),
        };
      }
      return { ...state, phase: "awaiting" };
    }

    case "SELECT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      const correct = action.symbol === state.round.symbol;
      return {
        ...state,
        phase: "graded",
        selectedSymbol: action.symbol,
        wasCorrect: correct,
        wasTimeout: false,
        correctCount: state.correctCount + (correct ? 1 : 0),
        sessionStats: addStat(state.sessionStats, state.round.symbol, correct),
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      return {
        ...state,
        phase: "graded",
        wasCorrect: false,
        wasTimeout: true,
        sessionStats: addStat(state.sessionStats, state.round.symbol, false),
      };
    }

    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selectedSymbol: null,
        wasCorrect: null,
        wasTimeout: false,
      };

    case "SESSION_DONE":
      return { ...state, phase: "done" };

    case "STOP":
      return { ...INITIAL_STATE };
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function IntervalExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const practiceMode = mode === "practice";
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const from = searchParams.get("from") ?? undefined;

  const [state, dispatch] = useReducer(drillIntervalReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const [audioPlayer, setAudioPlayer] = useState<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);

  const [activePip, setActivePip] = useState<number | null>(null);
  const [donePips, setDonePips] = useState<number[]>([]);
  const bassIndexRef = useRef<number>(0);

  const challengeStartedAtRef = useRef<string | null>(null);
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const responseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const { recordPlay } = useListenSession("intervals", listenMode, nodeId);
  const { recordItem } = useExerciseSession("intervals", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);
  const cadenceTempo = usePrefsStore((s) => s.cadenceTempo);
  const listenAutoAdvance = usePrefsStore((s) => s.listenAutoAdvance);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    setAudioPlayer(player);
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Challenge mode: load previous best on mount.
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    void getChallengeData("intervals").then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode]);

  // Record response start time when awaiting answer (for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    responseStartRef.current = Date.now();
  }, [state.phase]);

  // FSRS write + review record after grading (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    if (state.config.drillMode === "listening") return;
    const itemId = intervalDrillItemId(
      state.round.symbol,
      state.round.effectiveDirection,
      state.config.keyContext,
    );
    const correct = state.wasCorrect === true;
    const responseMs = responseStartRef.current
      ? Date.now() - responseStartRef.current
      : 8000;
    const chosenAnswerId =
      !correct && state.selectedSymbol !== null ? state.selectedSymbol : null;
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: state.config.fsrsEnabled });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (challengeMode) {
      challengeResponseTimesRef.current.push(responseMs);
      if (correct) {
        challengeCorrectRef.current += 1;
        const rating: Rating = responseMs < 5000 ? "good" : "hard";
        void applyReview(itemId, rating);
      } else {
        void applyReview(itemId, "again");
      }
      return;
    }
    if (!state.config.fsrsEnabled) return;
    void applyReview(itemId, correct ? "good" : "again");
  }, [state.phase, state.round, state.wasCorrect, state.selectedSymbol, state.config.fsrsEnabled, state.config.keyContext, state.config.drillMode, challengeMode, applyReview]);

  // Drill stats write after graded answer (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    void upsertDrillStats(
      intervalDrillStatsKey(state.config, state.round.symbol, state.round.effectiveDirection),
      "graded",
      state.wasCorrect ?? false,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Drill stats write after listening-mode reveal (fire-and-forget).
  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDrillStats(
      intervalDrillStatsKey(state.config, state.round.symbol, state.round.effectiveDirection),
      "listening",
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !listenMode || !state.round) return;
    recordPlay(intervalDrillItemId(state.round.symbol, state.round.effectiveDirection, state.config.keyContext));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Challenge mode: record attempt on session done.
  useEffect(() => {
    if (state.phase !== "done" || !challengeMode) return;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    const correct = challengeCorrectRef.current;
    void recordChallengeAttempt({
      challengeKey: "intervals",
      startedAt: challengeStartedAtRef.current ?? new Date().toISOString(),
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: Math.round(avgMs),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Challenge mode: auto-advance 600ms after timeout.
  useEffect(() => {
    if (!state.wasTimeout) return;
    const id = window.setTimeout(() => nextRound(), 600);
    return () => window.clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.wasTimeout]);

  // ── Note-start / note-end callbacks for StagePips ──

  const handleNoteStart = useCallback((event: { midi: number; voice?: string }) => {
    if (event.voice === "bass") {
      const idx = bassIndexRef.current;
      bassIndexRef.current = idx + 1;
      setActivePip(idx);
    }
  }, []);

  const handleNoteEnd = useCallback((event: { midi: number; voice?: string }) => {
    if (event.voice === "bass") {
      setActivePip(null);
      setDonePips((d) => [...d, d.length]);
    }
  }, []);

  // ── Imperative playback helpers ──

  const playRound = useCallback(
    async (round: IntervalExerciseRound, drillMode: IntervalExerciseConfig["drillMode"]) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      setActivePip(null);
      setDonePips([]);
      bassIndexRef.current = 0;
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan, {
        onNoteStart: handleNoteStart,
        onNoteEnd: handleNoteEnd,
      });
      setActivePip(null);

      recordItem(intervalDrillItemId(round.symbol, round.effectiveDirection, state.config.keyContext));
      if (drillMode === "listening") {
        dispatch({ type: "THINKING" });
      } else {
        dispatch({ type: "PLAYBACK_DONE" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleNoteStart, handleNoteEnd, recordItem, state.config.keyContext],
  );

  // Reveal: show visual answer immediately and play teachAudio.
  const handleReveal = useCallback(() => {
    const player = playerRef.current;
    const round = state.round;
    if (!round) return;
    dispatch({ type: "PLAYBACK_DONE" });
    if (player) void player.play(round.teachAudio);
  }, [state.round]);

  // Auto-reveal after thinking pause when listenAutoAdvance is on.
  useEffect(() => {
    if (state.phase !== "thinking" || !listenAutoAdvance) return;
    const t = window.setTimeout(handleReveal, 1500);
    return () => window.clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, listenAutoAdvance]);

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const playTogether = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(buildIntervalBlockPlan(state.round.lowerMidi, state.round.upperMidi));
  }, [state.round]);

  const startDrill = useCallback(
    (config: IntervalExerciseConfig, sessionLength: number) => {
      const round = buildIntervalExerciseRound(config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound, cadenceTempo],
  );

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildIntervalExerciseRound(state.config, nextRoundSeed(), CADENCE_TEMPO_OPTS[cadenceTempo]);
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound, cadenceTempo]);

  const handleSelect = useCallback(
    (symbol: IntervalSymbol) => {
      if (!state.round || state.phase !== "awaiting") return;
      const correct = symbol === state.round.symbol;

      dispatch({ type: "SELECT", symbol });

      if (uiSoundsEnabled) {
        if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
        uiSoundRef.current.play(correct ? "correct" : "wrong");
      }

      if (!correct && state.round.teachAudio) {
        const teachPlan = state.round.teachAudio;
        const player = playerRef.current;
        if (player) {
          window.setTimeout(() => {
            void player.play(teachPlan);
          }, 600);
        }
      }
    },
    [state.round, state.phase, uiSoundsEnabled],
  );

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs: challengeRemainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  // ── Keyboard shortcuts ──

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if (e.key.toLowerCase() === "t" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        playTogether();
        return;
      }
      if (state.phase === "thinking" && listenMode) {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          handleReveal();
        }
      } else if (state.phase === "graded" || state.phase === "reveal") {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          nextRound();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, listenMode, handleReveal, nextRound, replay, playTogether]);

  // ── Render ──

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <IntervalExerciseChallengeStart onStart={startDrill} />;
    if (coached) return <IntervalExerciseCoachStart nodeId={nodeId} itemCount={coachItemCount} onStart={startDrill} />;
    return <IntervalExerciseConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={avgMs}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(state.config, state.sessionLength)}
          onStop={handleStop}
        />
      );
    }
    return (
      <IntervalExerciseSummary
        config={state.config}
        sessionLength={state.sessionLength}
        correctCount={state.correctCount}
        sessionStats={state.sessionStats}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <IntervalExerciseLoop
      state={state}
      activePip={activePip}
      donePips={donePips}
      player={audioPlayer}
      listenMode={listenMode}
      listenAutoAdvance={listenAutoAdvance}
      challengeMode={challengeMode}
      practiceMode={practiceMode}
      challengeRemainingMs={challengeRemainingMs}
      modeColor={modeColor}
      onSelect={handleSelect}
      onReveal={handleReveal}
      onNext={nextRound}
      onReplay={replay}
      onTogether={playTogether}
      onStop={handleStop}
      from={from}
    />
  );
}

// ─── Listen mode auto-start ───────────────────────────────────────────────────

// ─── Coached listen auto-start ───────────────────────────────────────────────

function IntervalExerciseCoachStart({
  nodeId,
  itemCount,
  onStart,
}: {
  nodeId: string | undefined;
  itemCount: number;
  onStart: (config: IntervalExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    const isChromatic = nodeId === "chromatic-intervals";
    onStart(
      {
        ...DEFAULT_INTERVAL_EXERCISE_CONFIG,
        intervals: isChromatic ? [...INTERVAL_PRESETS.all] : [...INTERVAL_PRESETS.diatonic],
        tonic: "random",
        drillMode: "listening",
        fsrsEnabled: false,
      },
      itemCount,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Challenge mode auto-start ────────────────────────────────────────────────

function IntervalExerciseChallengeStart({
  onStart,
}: {
  onStart: (config: IntervalExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_INTERVAL_EXERCISE_CONFIG, drillMode: "graded", fsrsEnabled: false }, 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting challenge…</p>
    </div>
  );
}

// ─── Config screen ────────────────────────────────────────────────────────────

// ─── Config persistence ───────────────────────────────────────────────────────

const INTERVAL_DRILL_LS_KEY = "interval-drill-config-v3";

interface IntervalExerciseSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  solfegeFirst: boolean;
  direction: IntervalExerciseConfig["direction"];
  keyContext: IntervalExerciseConfig["keyContext"];
  tonicMidi: number;
  tonicMode: "fixed" | "random";
  bottomNote: IntervalExerciseConfig["bottomNote"];
  selectedGroups: GroupKey[];
}

const INTERVAL_DRILL_DEFAULTS: IntervalExerciseSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  solfegeFirst: false,
  direction: "ascending",
  keyContext: "in-key",
  tonicMidi: 60,
  tonicMode: "fixed",
  bottomNote: "tonic",
  selectedGroups: [...GROUP_PRESETS.diatonic],
};

function loadIntervalExerciseConfig(): IntervalExerciseSavedConfig {
  try {
    const raw = localStorage.getItem(INTERVAL_DRILL_LS_KEY);
    if (raw) return { ...INTERVAL_DRILL_DEFAULTS, ...(JSON.parse(raw) as Partial<IntervalExerciseSavedConfig>) };
  } catch { /* ignore */ }
  return INTERVAL_DRILL_DEFAULTS;
}

function saveIntervalExerciseConfig(cfg: IntervalExerciseSavedConfig): void {
  try { localStorage.setItem(INTERVAL_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────

// Preselect the interval groups for the lesson's node when arriving via
// "Practice this" (`?nodeId=…`), so a chromatic-intervals lesson opens on the
// full set rather than the persisted default. Mirrors `IntervalExerciseCoachStart`.
function intervalNodeConfigPatch(nodeId: string | undefined): Partial<IntervalExerciseSavedConfig> {
  if (nodeId === "chromatic-intervals") return { selectedGroups: [...GROUP_PRESETS.all] };
  if (nodeId === "diatonic-intervals-major") return { selectedGroups: [...GROUP_PRESETS.diatonic] };
  return {};
}

interface IntervalExerciseConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: IntervalExerciseConfig, sessionLength: number) => void;
}

function IntervalExerciseConfigScreen({ mode, onStart }: IntervalExerciseConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const [initConfig] = useState(() => ({ ...loadIntervalExerciseConfig(), ...intervalNodeConfigPatch(nodeId) }));

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );

  const modeColor = PICKER_MODE_COLOR[mode];
  const [solfegeFirst, setSolfegeFirst] = useState(initConfig.solfegeFirst);
  const [direction, setDirection] = useState<IntervalExerciseConfig["direction"]>(initConfig.direction);
  const [keyContext, setKeyContext] = useState<IntervalExerciseConfig["keyContext"]>(initConfig.keyContext);
  const [tonicMidi, setTonicMidi] = useState(initConfig.tonicMidi);
  const [tonicMode, setTonicMode] = useState<"fixed" | "random">(initConfig.tonicMode);
  const [bottomNote, setBottomNote] = useState<IntervalExerciseConfig["bottomNote"]>(initConfig.bottomNote);
  const [selectedGroups, setSelectedGroups] = useState<Set<GroupKey>>(
    () => new Set(initConfig.selectedGroups),
  );

  // History stats for checked intervals + current direction/tonic
  const [historyStats, setHistoryStats] = useState<
    Partial<Record<string, DrillStatsRecord | null>>
  >({});

  const configBase = useMemo(
    () => ({
      keyContext,
      tonic: tonicMode,
      fixedTonicMidi: tonicMidi,
    }),
    [keyContext, tonicMode, tonicMidi],
  );

  // Load history stats whenever checked groups, direction, or tonic changes.
  useEffect(() => {
    const intervals = groupsToIntervals(selectedGroups);
    const dirs: Array<Exclude<IntervalExerciseConfig["direction"], "mixed">> =
      direction === "mixed"
        ? ["ascending", "descending", "harmonic"]
        : [direction];

    const keys = intervals.flatMap((sym) =>
      dirs.map((dir) => intervalDrillStatsKey(configBase, sym, dir)),
    );

    void Promise.all(
      keys.map((k) => getDrillStats(k).then((s) => [k, s ?? null] as const)),
    ).then((entries) => setHistoryStats(Object.fromEntries(entries)));
  }, [selectedGroups, direction, configBase]);

  // Persist config on any change.
  useEffect(() => {
    saveIntervalExerciseConfig({
      sessionLength,
      mode,
      solfegeFirst,
      direction,
      keyContext,
      tonicMidi,
      tonicMode,
      bottomNote,
      selectedGroups: Array.from(selectedGroups),
    });
  }, [sessionLength, mode, solfegeFirst, direction, keyContext, tonicMidi, tonicMode, bottomNote, selectedGroups]);

  const applyPreset = (preset: keyof typeof GROUP_PRESETS) => {
    setSelectedGroups(new Set(GROUP_PRESETS[preset]));
  };

  const toggleGroup = (key: GroupKey) => {
    setSelectedGroups((prev) => {
      if (prev.has(key)) {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }
      return new Set([...prev, key]);
    });
  };

  const handleStart = () => {
    const intervals = groupsToIntervals(selectedGroups);
    if (intervals.length === 0) return;
    onStart(
      {
        intervals,
        direction,
        keyContext: solfegeFirst ? "in-key" : keyContext,
        tonic: tonicMode,
        fixedTonicMidi: tonicMidi,
        bottomNote: solfegeFirst ? "any-degree" : bottomNote,
        drillMode: solfegeFirst ? "solfege-first" : mode === "listen" ? "listening" : "graded",
        fsrsEnabled: mode === "practice" && !solfegeFirst,
      },
      sessionLength,
    );
  };

  const directionDisplay = (d: IntervalExerciseConfig["direction"]) =>
    ({ ascending: "Ascending", descending: "Descending", harmonic: "Harmonic", mixed: "Mixed" }[d]);

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Interval Recognition"
        subhead="A cadence sets the key, two notes play — name the interval."
      />

      <ModeSwitcherBar current={mode} exercisePath="/exercise/intervals" />
      <ExerciseLessonLink nodeIds={["diatonic-intervals-major", "chromatic-intervals"]} />

      {/* Session settings */}
      <div className="space-y-5 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={!showCustomLength && sessionLength === n}
                onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                className={`px-4 py-2 text-sm border transition-colors ${
                  !showCustomLength && sessionLength === n
                    ? ""
                    : "bg-paper text-ink border-ink/20 hover:border-ink"
                }`}
                style={!showCustomLength && sessionLength === n ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className={`px-4 py-2 text-sm border transition-colors ${
                showCustomLength
                  ? ""
                  : "bg-paper text-ink border-ink/20 hover:border-ink"
              }`}
              style={showCustomLength ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

        {(mode === "practice" || mode === "review") && (
          <div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={solfegeFirst}
                onChange={(e) => setSolfegeFirst(e.target.checked)}
                className="accent-ink w-3.5 h-3.5"
                aria-label="Show solfège context in feedback"
              />
              <span className="font-mono text-sm text-ink">Solfège context</span>
            </label>
            <p className="mt-1.5 font-serif italic text-xs text-inkSoft">
              Shows degree names (e.g. do → sol) in feedback after each answer. Forces in-key, random tonic.
            </p>
          </div>
        )}

        <ConfigRow label="Direction">
          <NativeSelect
            value={direction}
            onChange={(v) => setDirection(v as IntervalExerciseConfig["direction"])}
            options={[
              { value: "ascending",  label: directionDisplay("ascending") },
              { value: "descending", label: directionDisplay("descending") },
              { value: "harmonic",   label: directionDisplay("harmonic") },
              { value: "mixed",      label: directionDisplay("mixed") },
            ]}
          />
        </ConfigRow>

        {!solfegeFirst && (
          <ConfigRow label="Key context">
            <NativeSelect
              value={keyContext}
              onChange={(v) => setKeyContext(v as IntervalExerciseConfig["keyContext"])}
              options={[
                { value: "in-key",   label: "In key  (cadence first)" },
                { value: "abstract", label: "Abstract  (no key)" },
              ]}
            />
          </ConfigRow>
        )}

        {(solfegeFirst || keyContext === "in-key") && (
          <>
            <ConfigRow label="Tonic">
              <NativeSelect
                value={tonicMode === "random" ? "random" : String(tonicMidi)}
                onChange={(v) => {
                  if (v === "random") {
                    setTonicMode("random");
                  } else {
                    setTonicMode("fixed");
                    setTonicMidi(parseInt(v, 10));
                  }
                }}
                options={[
                  ...TONIC_OPTIONS.map(({ midi, label }) => ({
                    value: String(midi),
                    label,
                  })),
                  { value: "random", label: "Random" },
                ]}
              />
            </ConfigRow>

            {!solfegeFirst && (
              <ConfigRow label="Bottom note">
                <NativeSelect
                  value={bottomNote}
                  onChange={(v) => setBottomNote(v as IntervalExerciseConfig["bottomNote"])}
                  options={[
                    { value: "tonic",      label: "From tonic  (simpler)" },
                    { value: "any-degree", label: "Any diatonic degree  (harder)" },
                  ]}
                />
              </ConfigRow>
            )}
          </>
        )}
      </div>

      {/* Interval pool */}
      <div className="mb-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Interval pool
        </p>

        {/* Quick-select shortcuts */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {(["consonants", "diatonic", "all"] as const).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => applyPreset(preset)}
              className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors capitalize"
            >
              {preset}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSelectedGroups(new Set())}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-inkFaded hover:border-ink hover:text-ink transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Group checkboxes */}
        <div className="border border-ink/10">
          {INTERVAL_GROUPS.map((group) => {
            const checked = selectedGroups.has(group.key);
            return (
              <label
                key={group.key}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-ink/10 last:border-b-0 ${
                  checked ? "bg-ink/[0.02] text-ink" : "text-inkFaded"
                } hover:bg-ink/5`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleGroup(group.key)}
                  className="accent-ink w-3.5 h-3.5 shrink-0"
                  aria-label={`Include ${group.label}`}
                />
                <span className="font-mono text-sm font-medium w-36 shrink-0">{group.label}</span>
                <span className="font-serif italic text-xs text-inkFaded">
                  {group.symbols.map((sym) => INTERVAL_POOL[sym].longName).join(", ")}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* History table */}
      <HistoryTable
        intervals={groupsToIntervals(selectedGroups)}
        direction={direction}
        historyStats={historyStats}
      />

      <button
        type="button"
        onClick={handleStart}
        disabled={selectedGroups.size === 0}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface IntervalExerciseLoopProps {
  state: IntervalExerciseState;
  activePip: number | null;
  donePips: number[];
  player: AudioPlayer | null;
  listenMode: boolean;
  listenAutoAdvance: boolean;
  challengeMode: boolean;
  practiceMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  onSelect: (symbol: IntervalSymbol) => void;
  onReveal: () => void;
  onNext: () => void;
  onReplay: () => void;
  onTogether: () => void;
  onStop: () => void;
  from?: string;
}

function IntervalExerciseLoop({
  state,
  activePip,
  donePips,
  listenMode,
  listenAutoAdvance,
  challengeMode,
  practiceMode,
  challengeRemainingMs,
  modeColor,
  onSelect,
  onReveal,
  onNext,
  onReplay,
  onTogether,
  onStop,
  from,
}: IntervalExerciseLoopProps): ReactNode {
  const {
    phase, round, roundNumber, sessionLength, config,
    selectedSymbol, wasCorrect, wasTimeout,
  } = state;

  const isPlaying        = phase === "playing";
  const isThinking       = phase === "thinking";
  const isAwaiting       = phase === "awaiting";
  const isReveal = phase === "reveal";
  const isGraded = phase === "graded";

  // Hold the auto-advance countdown until the reveal (teach) audio has finished
  // playing, so the next round's cadence never starts over the tail of this
  // round's final chord. The teach audio begins when the reveal phase starts.
  const [revealAudioDone, setRevealAudioDone] = useState(false);
  useEffect(() => {
    if (!isReveal || !round) {
      setRevealAudioDone(false);
      return;
    }
    setRevealAudioDone(false);
    const holdMs = round.teachAudio.totalDuration * 1000 + 250;
    const t = window.setTimeout(() => setRevealAudioDone(true), holdMs);
    return () => window.clearTimeout(t);
  }, [isReveal, round]);

  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal || isThinking ? roundNumber : roundNumber - 1;

  // Sorted interval buttons (only checked intervals, semitone order).
  const intervalButtons = INTERVAL_SYMBOLS_ORDERED.filter((s) =>
    config.intervals.includes(s),
  );

  const showBothNotes = isReveal || isGraded;
  const scoreNotes = useMemo(
    () => {
      if (!round) return null;
      if (showBothNotes) return buildIntervalVexNote(round.lowerMidi, round.upperMidi);
      return [{ keys: [midiToVexKey(round.lowerMidi)], duration: "w" as const }];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [round?.lowerMidi, round?.upperMidi, showBothNotes],
  );

  const scoreAriaLabel =
    round && showBothNotes
      ? `${INTERVAL_POOL[round.symbol].longName} interval (${round.symbol}) — ${midiToNoteName(round.lowerMidi)} to ${midiToNoteName(round.upperMidi)}`
      : round
      ? `Bottom note: ${midiToNoteName(round.lowerMidi)}`
      : "Interval staff";

  const directionLabel =
    round
      ? { ascending: "↑ ascending", descending: "↓ descending", harmonic: "⊙ harmonic" }[
          round.effectiveDirection
        ]
      : "";

  const contextLabel = config.keyContext === "abstract"
    ? "Abstract"
    : `Key: ${round?.keyName ?? ""}`;

  // Solfège label shown on reveal: "do → sol" when both notes are diatonic,
  // "from do" when only the lower note is known.
  const solfegeLabel =
    round && round.degreeIndex !== null
      ? round.upperDegreeIndex !== null
        ? `${IONIAN_SOLFEGE[round.degreeIndex]} → ${IONIAN_SOLFEGE[round.upperDegreeIndex]}`
        : `from ${IONIAN_SOLFEGE[round.degreeIndex]}`
      : null;

  const useGrid3 = intervalButtons.length >= 9;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      {/* Back links */}
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {challengeMode && isAwaiting && (
        <ChallengeTimer totalMs={8000} remainingMs={challengeRemainingMs} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Interval Drill</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">
        {contextLabel}
        {directionLabel ? ` · ${directionLabel}` : ""}
      </p>

      {/* Cadence pips / playing indicator */}
      <div className="h-[3px] mb-3">
        {config.keyContext === "in-key" && isPlaying && (
          <StagePips count={CADENCE_PIP_COUNT} active={activePip} done={donePips} />
        )}
        {config.keyContext === "abstract" && isPlaying && (
          <div className="h-[3px] w-full bg-accent/30 animate-pulse" />
        )}
      </div>

      {/* Progress bar */}
      <div
        className="w-full h-0.5 bg-ink/10 mb-2"
        role="progressbar"
        aria-valuenow={roundNumber}
        aria-valuemin={1}
        aria-valuemax={sessionLength}
      >
        <div
          className="h-full transition-all duration-300"
          style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }}
        />
      </div>

      {/* Counter */}
      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>{state.correctCount} correct of {answeredCount} answered</span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        <span className="capitalize">{config.direction}</span>
      </div>

      {/* Staff — lower note only while playing; both notes once the interval is played */}
      {scoreNotes && (
        <div className="mb-4">
          <Score
            notes={scoreNotes}
            width={200}
            height={130}
            aria-label={scoreAriaLabel}
          />
        </div>
      )}

      {/* Prompt */}
      <div className="mb-6 min-h-[3rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">
            Listen…
          </p>
        )}

        {/* Interval question */}
        {isAwaiting && (
          <div aria-live="polite">
            <p className="font-serif italic text-xl text-ink">
              What interval did you hear?
            </p>
          </div>
        )}

        {/* Reveal / graded */}
        {(isReveal || isGraded) && round && (
          <div aria-live="polite">
            {isReveal && (
              <p className="font-serif italic text-xl text-ink">
                That was{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {INTERVAL_POOL[round.symbol].longName}
                </strong>
                {solfegeLabel && (
                  <span className="text-base text-inkSoft ml-1">({solfegeLabel})</span>
                )}
                .
              </p>
            )}
            {isGraded && wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                Correct —{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {INTERVAL_POOL[round.symbol].longName}
                </strong>
                {solfegeLabel && (
                  <span className="text-base text-inkSoft ml-1">({solfegeLabel})</span>
                )}
                .
              </p>
            )}
            {isGraded && !wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                {wasTimeout ? "Time! " : "The answer was "}{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {INTERVAL_POOL[round.symbol].longName}
                </strong>
                {solfegeLabel && (
                  <span className="text-base text-inkSoft ml-1">({solfegeLabel})</span>
                )}
                .
              </p>
            )}
            {isGraded && !challengeMode && (
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1"
                 style={{ color: practiceMode ? modeColor : "#9A9390" }}>
                {practiceMode ? "Counts toward Today." : "No schedule changes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Interval answer buttons */}
      <div className="mb-6">
          <div
            className={`grid gap-2 mb-2 ${
              useGrid3 ? "grid-cols-3" : "grid-cols-2"
            }`}
          >
            {intervalButtons.map((sym) => {
              const isCorrect = sym === round?.symbol;
              const isSelected = sym === selectedSymbol;
              const showCorrect = (isGraded || isReveal) && isCorrect;
              const showWrong = isGraded && isSelected && !isCorrect;
              const disabled = isPlaying || isThinking || isReveal || isGraded;

              return (
                <button
                  key={sym}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(sym)}
                  aria-pressed={isSelected && isAwaiting}
                  aria-label={`${INTERVAL_POOL[sym].longName}, ${sym}`}
                  className={`px-3 py-3 border text-left transition-colors ${
                    showCorrect
                      ? "bg-success/10 border-success text-success"
                      : showWrong
                      ? "bg-danger/10 border-danger text-danger"
                      : disabled
                      ? "bg-paper border-ink/10 text-inkFaded"
                      : "bg-paper border-ink/20 text-ink hover:border-ink hover:bg-ink/5"
                  }`}
                >
                  <div className="font-serif text-sm font-medium leading-tight">
                    {INTERVAL_POOL[sym].longName}
                  </div>
                  <div className="font-mono text-[10px] text-inkFaded mt-0.5">{sym}</div>
                </button>
              );
            })}
          </div>
          {isThinking && listenMode && !listenAutoAdvance && (
            <p className="mt-2 font-mono text-[10px] text-inkFaded">Space/Enter to reveal · R replay · T together</p>
          )}
          {isAwaiting && (
            <p className="mt-2 font-mono text-[10px] text-inkFaded">R replay · T together</p>
          )}
          {(isReveal || isGraded) && (
            <p className="mt-2 font-mono text-[10px] text-inkFaded">
              {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R · T
            </p>
          )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        {listenMode && isThinking && !listenAutoAdvance && (
          <button
            type="button"
            onClick={onReveal}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
            aria-keyshortcuts="Space Enter"
          >
            Reveal (Space)
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={!isReveal && !isGraded}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying || isThinking}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the interval"
          >
            ♩ Replay
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying || isThinking}
            onClick={onTogether}
            className={buttonClasses("secondary")}
            aria-label="Play both notes together as a block chord"
            aria-keyshortcuts="T"
          >
            ⊞ Together
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal && revealAudioDone} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface IntervalExerciseSummaryProps {
  config: IntervalExerciseConfig;
  sessionLength: number;
  correctCount: number;
  sessionStats: Partial<Record<IntervalSymbol, SessionStat>>;
  onAgain: () => void;
  onStop: () => void;
}

function IntervalExerciseSummary({
  config,
  sessionLength,
  correctCount,
  sessionStats,
  onAgain,
  onStop,
}: IntervalExerciseSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode === "graded";
  const accuracy = isGraded ? Math.round((correctCount / sessionLength) * 100) : null;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="interval"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">
            Exercises
          </p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {accuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">
              Correct
            </p>
            <p className="font-serif text-3xl font-medium text-ink">
              {accuracy}
              <span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      {/* Per-interval breakdown */}
      {Object.keys(sessionStats).length > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
            By interval
          </p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Interval</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>}
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>}
            </div>
            {INTERVAL_SYMBOLS_ORDERED.filter((s) => sessionStats[s] !== undefined).map((sym) => {
              const stat = sessionStats[sym]!;
              const pct = stat.played > 0 && stat.correct > 0
                ? Math.round((stat.correct / stat.played) * 100)
                : stat.played > 0 ? 0 : null;
              return (
                <div
                  key={sym}
                  className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0"
                >
                  <div>
                    <span className="font-serif text-sm text-ink">{INTERVAL_POOL[sym].longName}</span>
                    <span className="font-mono text-xs text-inkFaded ml-2">{sym}</span>
                  </div>
                  <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.played}</span>
                  {isGraded && <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.correct}</span>}
                  {isGraded && (
                    <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <button
          type="button"
          onClick={onAgain}
          className={buttonClasses("primary")}
        >
          Drill again →
        </button>
        <button
          type="button"
          onClick={onStop}
          className={buttonClasses("secondary")}
        >
          Change settings
        </button>
        <Link
          to="/"
          className={buttonClasses("secondary")}
        >
          Today
        </Link>
        <Link
          to="/stats"
          className={buttonClasses("secondary")}
        >
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={["diatonic-intervals-major", "chromatic-intervals"]} />
      </div>
    </div>
  );
}

// ─── History table ────────────────────────────────────────────────────────────

interface HistoryTableProps {
  intervals: readonly IntervalSymbol[];
  direction: IntervalExerciseConfig["direction"];
  historyStats: Partial<Record<string, DrillStatsRecord | null>>;
}

function HistoryTable({ intervals, direction, historyStats }: HistoryTableProps): ReactNode {
  const dirs: Array<Exclude<IntervalExerciseConfig["direction"], "mixed">> =
    direction === "mixed"
      ? ["ascending", "descending", "harmonic"]
      : [direction];

  const rows: Array<{
    sym: IntervalSymbol;
    dir: Exclude<IntervalExerciseConfig["direction"], "mixed">;
    stats: DrillStatsRecord | null;
  }> = intervals.flatMap((sym) =>
    dirs.map((dir) => {
      const key = `interval-drill|${sym}|${dir}`;
      // Find any key in historyStats that starts with this prefix
      const matchKey = Object.keys(historyStats).find((k) => k.startsWith(key));
      return { sym, dir, stats: matchKey ? (historyStats[matchKey] ?? null) : null };
    }),
  );

  const hasAnyData = rows.some((r) => r.stats && r.stats.played > 0);

  const [open, setOpen] = useState(false);
  if (!hasAnyData) return null;
  return (
    <div className="mb-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-3"
        aria-expanded={open}
      >
        History
        <span aria-hidden className="text-[8px]">{open ? "▴" : "▾"}</span>
      </button>
      <div className={open ? "border border-ink/10" : "hidden"}>
        <div className="grid grid-cols-[1fr_6rem_4rem_4rem_4rem_3rem] px-4 py-2 border-b border-ink/10">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Interval</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Direction</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Listen</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Practice</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
        </div>
        {rows
          .filter((r) => r.stats && r.stats.played > 0)
          .map(({ sym, dir, stats }) => {
            const s = stats!;
            const listened = Math.max(0, s.played - s.correct - s.errors);
            const graded   = s.correct + s.errors;
            const pct      = graded > 0 ? Math.round((s.correct / graded) * 100) : null;
            return (
              <div
                key={`${sym}|${dir}`}
                className="grid grid-cols-[1fr_6rem_4rem_4rem_4rem_3rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0"
              >
                <div>
                  <span className="font-serif text-sm text-ink">{INTERVAL_POOL[sym].longName}</span>
                  <span className="font-mono text-xs text-inkFaded ml-2">{sym}</span>
                </div>
                <span className="font-mono text-xs text-inkFaded capitalize">{dir}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{listened}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">{graded}</span>
                <span className="font-mono text-xs text-ink text-right tabular-nums">
                  {graded > 0 ? s.correct : "—"}
                </span>
                <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                  {pct !== null ? `${pct}%` : "—"}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ─── VexFlow notation helpers ─────────────────────────────────────────────────

const SEMI_TO_VEX: Record<number, { letter: string; acc: string }> = {
  0:  { letter: "c", acc: "" },
  1:  { letter: "d", acc: "b" },
  2:  { letter: "d", acc: "" },
  3:  { letter: "e", acc: "b" },
  4:  { letter: "e", acc: "" },
  5:  { letter: "f", acc: "" },
  6:  { letter: "f", acc: "#" },
  7:  { letter: "g", acc: "" },
  8:  { letter: "a", acc: "b" },
  9:  { letter: "a", acc: "" },
  10: { letter: "b", acc: "b" },
  11: { letter: "b", acc: "" },
};

function midiToVexKey(midi: number): string {
  const semi = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  const { letter, acc } = SEMI_TO_VEX[semi]!;
  return `${letter}${acc}/${oct}`;
}

function buildIntervalVexNote(lowerMidi: number, upperMidi: number): VexNote[] {
  return [
    {
      keys: [midiToVexKey(lowerMidi), midiToVexKey(upperMidi)],
      duration: "w" as const,
    },
  ];
}

// Flat-biased names for aria-labels.
const MIDI_NOTE_NAMES = ["C", "D♭", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];

function midiToNoteName(midi: number): string {
  const semi = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  return `${MIDI_NOTE_NAMES[semi]}${oct}`;
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">
        {label}
      </p>
      {children}
    </div>
  );
}

interface SelectOption {
  value: string;
  label: string;
}

function NativeSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}): ReactNode {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-8 py-2 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">
        ▾
      </span>
    </div>
  );
}
